import { doc, setDoc } from 'firebase/firestore';
import { useState } from 'react';
import { getLoadingKey } from '../../components/TenderDetails/utils';
import {
  uploadDraftForTenderReview,
  uploadSupportDocForTender,
  uploadTenderForm,
} from '../../services/api';
import { firestore } from '../../services/firebase';
import storageService from '../../services/storageService';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';
import { logDocumentActivity } from '../../utils/activityLogger';
import { validateTenderUpload } from '../../utils/storageValidation';

const EMPTY_OBJECT: Record<string, never> = {};

export interface UseSupportDocsUploadProps {
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  setLoadingStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setTender: React.Dispatch<React.SetStateAction<Tender | null>>;
  setDocumentCategories: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export const useSupportDocsUpload = ({
  tender,
  account,
  showSnackbar,
  setLoadingStates,
  setTender,
  setDocumentCategories,
}: UseSupportDocsUploadProps) => {
  const [processingFiles, setProcessingFiles] = useState<{
    support: string[];
    review: string[];
    form: string[];
    partner_doc: string[];
  }>({ support: [], review: [], form: [], partner_doc: [] });

  const [uploadValidationWarning, setUploadValidationWarning] = useState<{
    support?: string;
    form?: string;
    review?: string;
  }>({});

  const [isDraggingAdhoc, setIsDraggingAdhoc] = useState(false);
  const [isDraggingReview, setIsDraggingReview] = useState(false);
  const [isDraggingForm, setIsDraggingForm] = useState(false);

  const handleFileUpload = async (
    file: File,
    uploadType: string,
    options?: {
      isStrictForm?: boolean;
      collectionType?: string;
      saveToGlobal?: boolean;
      destinationFolder?: string;
    }
  ) => {
    if (
      !tender?.id ||
      !file ||
      !uploadType ||
      !account?.uid ||
      !account?.clientId ||
      !account?.actualDefaultBucketName
    ) {
      showSnackbar(
        'Missing critical information for file upload (user, client, tender, or GCS config).',
        'error'
      );
      return;
    }

    const validationType =
      uploadType === 'support' ? 'support' : uploadType === 'tender_form' ? 'tender_form' : 'draft';

    try {
      const validation = await validateTenderUpload(
        account.uid,
        tender.id,
        account.clientId,
        [file],
        validationType
      );
      if (!validation.allowed) {
        showSnackbar(validation.reason || 'Upload limit exceeded', 'error', 5000);
        return;
      }

      if (validation.warningMessage) {
        setUploadValidationWarning((prev) => ({
          ...prev,
          [uploadType === 'support' ? 'support' : uploadType === 'tender_form' ? 'form' : 'review']:
            validation.warningMessage,
        }));
      }
    } catch (_validationError) {
      // ignore
    }

    const loadingKey = getLoadingKey(
      tender.id,
      'upload',
      uploadType === 'support'
        ? 'support'
        : uploadType === 'tender_form'
          ? 'form'
          : uploadType === 'partner_doc'
            ? 'partner_doc'
            : 'review_draft'
    );
    setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }));
    setProcessingFiles((prev) => {
      if (uploadType === 'support') {
        return { ...prev, support: [...prev.support, file.name] };
      }
      if (uploadType === 'review_draft') {
        return { ...prev, review: [...prev.review, file.name] };
      }
      if (uploadType === 'tender_form') {
        return { ...prev, form: [...prev.form, file.name] };
      }
      if (uploadType === 'partner_doc') {
        return { ...prev, partner_doc: [...prev.partner_doc, file.name] };
      }
      return prev;
    });

    try {
      let response: { success?: boolean; message?: string } | undefined;
      if (uploadType === 'support' || uploadType === 'partner_doc') {
        let docType = uploadType === 'partner_doc' ? 'partner_doc' : 'other';

        if (uploadType !== 'partner_doc') {
          if (options?.collectionType) {
            docType = options.collectionType;
          } else {
            const lowerName = file.name.toLowerCase();
            if (/cv|resume/i.test(lowerName)) {
              docType = 'cv';
            } else if (/case\s?study/i.test(lowerName)) {
              docType = 'case_study';
            }
          }
        }

        // Standard Tender Upload
        response = await uploadSupportDocForTender({
          userId: account.uid,
          tenderId: tender.id,
          file,
          clientId: account.clientId,
          actualBucketName: account.actualDefaultBucketName,
          docType,
        });

        // Global Knowledge Base Sync
        if (options?.saveToGlobal && options?.collectionType && uploadType === 'support') {
          const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const cleanedFolder = (options?.destinationFolder || '').replace(/^\/+|\/+$/g, '').trim();
          const folderPath = cleanedFolder ? `${cleanedFolder}/` : '';
          const storagePath = `clients/${account.clientId}/knowledge_base/${options.collectionType}/${folderPath}${safeFileName}`;
          await storageService.uploadFile(storagePath, file);

          const msgBuffer = new TextEncoder().encode(storagePath);
          const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const docId = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

          const isSharepointStorage = account.storageType === 'sharepoint';
          const docRef = doc(firestore, 'clients', account.clientId, 'knowledge_documents', docId);
          await setDoc(
            docRef,
            {
              docId,
              fileName: file.name,
              sourceUrl: storagePath,
              storageType: isSharepointStorage ? 'sharepoint' : 'gcs',
              collectionType: options.collectionType,
              syncStatus: 'pending',
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        }
      } else if (uploadType === 'review_draft') {
        response = await uploadDraftForTenderReview({
          userId: account.uid,
          tenderId: tender.id,
          file,
          clientId: account.clientId,
          actualBucketName: account.actualDefaultBucketName,
        });
      } else if (uploadType === 'tender_form') {
        response = await uploadTenderForm({
          userId: account.uid,
          tenderId: tender.id,
          file,
          clientId: account.clientId,
          actualBucketName: account.actualDefaultBucketName,
          isStrictForm: options?.isStrictForm,
        });
      } else {
        throw new Error(`Upload type "${uploadType}" is not handled by this dialog.`);
      }

      const successResponse = response as { success?: boolean; message?: string };

      if (successResponse?.success) {
        logDocumentActivity.uploaded({
          type: uploadType,
          fileName: file.name,
          fileSize: file.size,
          location: `tender/${tender.id}`,
        });
        setProcessingFiles((prev) => ({
          review: prev.review.filter((f) => f !== file.name),
          support: prev.support.filter((f) => f !== file.name),
          form: prev.form.filter((f) => f !== file.name),
          partner_doc: prev.partner_doc.filter((f) => f !== file.name),
        }));
        if (uploadType === 'review_draft') {
          const docIdentifier = file.name.replace(/\.[^/.]+$/, '');
          setDocumentCategories((prev) => ({ ...prev, [docIdentifier]: 'draft' }));
          setTender((prev) => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              reviewResponse: {
                ...(prev.reviewResponse || EMPTY_OBJECT),
                status: 'Uploading draft...',
                lastType: 'final',
              },
            };
          });
        } else if (uploadType === 'support') {
          const docIdentifier = file.name.replace(/\.[^/.]+$/, '');
          const lowerName = file.name.toLowerCase();
          let docType = 'other';
          if (/cv|resume/i.test(lowerName)) {
            docType = 'cv';
          } else if (/case\s?study/i.test(lowerName)) {
            docType = 'case_study';
          }
          setDocumentCategories((prev) => ({ ...prev, [docIdentifier]: docType }));
        }
      } else {
        throw new Error(successResponse?.message || 'Upload processing failed.');
      }
    } catch (error: unknown) {
      const err = error as { error?: string; message?: string };
      if (err.error === 'Limit Exceeded' && err.message) {
        showSnackbar(`⚠️ ${err.message}`, 'warning', 7000);
      } else {
        showSnackbar(
          `Failed to upload ${file.name}. ${err.message || 'An unknown error occurred.'}`,
          'error'
        );
      }
      setProcessingFiles((prev) => ({
        review: prev.review.filter((f) => f !== file.name),
        support: prev.support.filter((f) => f !== file.name),
        form: prev.form.filter((f) => f !== file.name),
        partner_doc: prev.partner_doc.filter((f) => f !== file.name),
      }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const commonDragEvents = (
    event: React.DragEvent,
    setDraggingState: (dragging: boolean) => void
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDraggingState(true);
    } else if (event.type === 'dragleave') {
      setDraggingState(false);
    }
  };

  const commonDropHandler = (
    event: React.DragEvent,
    setDraggingState: (dragging: boolean) => void,
    uploadType: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingState(false);

    if (!event.dataTransfer.files || event.dataTransfer.files.length === 0) {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    for (const file of files) {
      handleFileUpload(file, uploadType);
    }
  };

  const handleDragEventsReview = (e: React.DragEvent) => commonDragEvents(e, setIsDraggingReview);
  const handleDropReview = (e: React.DragEvent) =>
    commonDropHandler(e, setIsDraggingReview, 'review_draft');
  const handleDragEventsAdhoc = (e: React.DragEvent) => commonDragEvents(e, setIsDraggingAdhoc);
  const handleDropAdhoc = (e: React.DragEvent) =>
    commonDropHandler(e, setIsDraggingAdhoc, 'support');
  const handleDragEventsForm = (e: React.DragEvent) => commonDragEvents(e, setIsDraggingForm);
  const handleDropForm = (e: React.DragEvent) =>
    commonDropHandler(e, setIsDraggingForm, 'tender_form');

  return {
    processingFiles,
    setProcessingFiles,
    uploadValidationWarning,
    isDraggingAdhoc,
    isDraggingReview,
    isDraggingForm,
    handleFileUpload,
    commonDragEvents,
    handleDragEventsReview,
    handleDropReview,
    handleDragEventsAdhoc,
    handleDropAdhoc,
    handleDragEventsForm,
    handleDropForm,
  };
};
